import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import {
  useOrgParameterDefaults,
  useCreateOrgParameterDefault,
  useDeleteOrgParameterDefault,
} from "@/hooks/use-org-parameter-defaults";
import { useOrgParameters } from "@/hooks/use-parameters";
import { useLabs } from "@/hooks/use-labs";
import { useAnalysisMethods } from "@/hooks/use-analysis-methods";
import { OrgDefaultsTable } from "@/components/labs/org-defaults-table";
import {
  OrgDefaultsForm,
  type OrgDefaultsFormSubmitPayload,
} from "@/components/labs/org-defaults-form";
import { OrgDefaultsEditModal } from "@/components/labs/org-defaults-edit-modal";
import { MethodsFilters, type MethodsFilterValues } from "@/components/labs/methods-filters";
import { MethodsTable } from "@/components/labs/methods-table";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Breadcrumbs, Button, EmptyState, PageHeader, SkeletonTable } from "@/components/ui";
import { Tabs, type TabDef } from "@/components/ui/tabs";
import { type components } from "@njbsoft/types";

type LabDefaultsTab = "defaults" | "methods";

type OrgParameterDefault = components["schemas"]["OrgParameterDefault"];

export const Route = createFileRoute("/_authenticated/labs/defaults")({
  component: OrgParameterDefaultsPage,
});

function OrgParameterDefaultsPage() {
  const { t } = useTranslation(["orgDefaults", "org", "common"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const [activeTab, setActiveTab] = useState<LabDefaultsTab>("defaults");
  const [showForm, setShowForm] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<OrgParameterDefault | null>(null);
  const [pendingDelete, setPendingDelete] = useState<OrgParameterDefault | null>(null);
  const [methodsFilters, setMethodsFilters] = useState<MethodsFilterValues>({});

  const listQuery = useOrgParameterDefaults(orgId);
  const createMut = useCreateOrgParameterDefault(orgId);
  const deleteMut = useDeleteOrgParameterDefault(orgId);

  const parametersQuery = useOrgParameters(orgId, { limit: 100 });
  const labsQuery = useLabs(orgId, { status: "active", limit: 100 });
  // `limit: 20` powers the methods OPTIONS for the org-defaults form (server
  // search pagination); the Methods tab itself uses `methodsBrowseQuery` below
  // with a user-controlled filter state.
  const methodsQuery = useAnalysisMethods({ limit: 20 });
  const methodsBrowseQuery = useAnalysisMethods({
    search: methodsFilters.search,
    limit: 100,
  });

  const parameterOptions = useMemo(
    () => (parametersQuery.data?.parameters ?? []).map((p) => ({ value: p.id, label: p.name })),
    [parametersQuery.data],
  );
  const labOptions = useMemo(
    () => (labsQuery.data?.items ?? []).map((l) => ({ value: l.id, label: l.name })),
    [labsQuery.data],
  );
  const methodOptions = useMemo(
    () =>
      (methodsQuery.data?.analysis_methods ?? []).map((m) => ({
        value: m.id,
        label: `${m.code} — ${m.name}`,
      })),
    [methodsQuery.data],
  );

  const parameterNamesById = useMemo(
    () => Object.fromEntries(parameterOptions.map((o) => [o.value, o.label])),
    [parameterOptions],
  );
  const labNamesById = useMemo(
    () => Object.fromEntries(labOptions.map((o) => [o.value, o.label])),
    [labOptions],
  );
  const methodNamesById = useMemo(
    () => Object.fromEntries(methodOptions.map((o) => [o.value, o.label])),
    [methodOptions],
  );

  const rows = listQuery.data?.items ?? [];

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const handleSubmit = async (payload: OrgDefaultsFormSubmitPayload) => {
    await createMut.mutateAsync({
      parameter_id: payload.parameter_id,
      program: payload.program,
      default_lab_id: payload.default_lab_id,
      default_method_id: payload.default_method_id,
    });
    setShowForm(false);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await deleteMut.mutateAsync(pendingDelete.id);
    setPendingDelete(null);
  };

  const methodRows = methodsBrowseQuery.data?.analysis_methods ?? [];

  const tabDefs: TabDef<LabDefaultsTab>[] = [
    { value: "defaults", label: t("tabs.defaults") },
    { value: "methods", label: t("tabs.methods") },
  ];

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("title") }]} />
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          activeTab === "defaults" && !showForm ? (
            <Button onClick={() => setShowForm(true)}>{t("addDefault")}</Button>
          ) : undefined
        }
      />

      <div className="mt-6">
        <Tabs
          tabs={tabDefs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("tabs.ariaLabel")}
        />
      </div>

      {activeTab === "defaults" && (
        <div role="tabpanel" id="tabpanel-defaults" aria-labelledby="tab-defaults" className="mt-4">
          {showForm && (
            <div className="mt-2">
              <OrgDefaultsForm
                parameterOptions={parameterOptions}
                labOptions={labOptions}
                methodOptions={methodOptions}
                onSubmit={handleSubmit}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}

          {listQuery.isLoading ? (
            <div className="mt-4">
              <SkeletonTable columns={5} rows={3} />
            </div>
          ) : rows.length === 0 ? (
            <div className="mt-4">
              <EmptyState title={t("empty.title")} description={t("empty.description")} />
            </div>
          ) : (
            <div className="mt-4">
              <OrgDefaultsTable
                rows={rows}
                parameterNamesById={parameterNamesById}
                labNamesById={labNamesById}
                methodNamesById={methodNamesById}
                onEdit={(row) => setPendingEdit(row)}
                onDelete={(row) => setPendingDelete(row)}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === "methods" && (
        <div role="tabpanel" id="tabpanel-methods" aria-labelledby="tab-methods" className="mt-4">
          <div className="rounded-md bg-primary/10 p-3 text-sm text-on-surface">
            {t("methods.banner")}
          </div>
          <div className="mt-4">
            <MethodsFilters filters={methodsFilters} onChange={setMethodsFilters} />
          </div>
          {methodsBrowseQuery.isLoading ? (
            <div className="mt-4">
              <SkeletonTable columns={7} rows={5} />
            </div>
          ) : methodRows.length === 0 ? (
            <div className="mt-4">
              <EmptyState title={t("methods.empty")} />
            </div>
          ) : (
            <div className="mt-4">
              <MethodsTable rows={methodRows} />
            </div>
          )}
        </div>
      )}

      <OrgDefaultsEditModal
        open={!!pendingEdit}
        onClose={() => setPendingEdit(null)}
        orgId={orgId}
        row={pendingEdit}
        parameterOptions={parameterOptions}
        labOptions={labOptions}
        methodOptions={methodOptions}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title={t("deleteConfirm.title")}
        message={t("deleteConfirm.body")}
        confirmLabel={t("common:delete")}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
